<template>
  <div class="pos-container" v-loading="loading">
    <PosHeader />
    
    <div class="main-container">
      <!-- 左侧面板 -->
      <div class="left-panel">
        <SearchArea @search="handleSearch" @add-product="handleAddProduct" />
        <CartList
          :cart-items="cart"
          @modify-qty="handleModifyQty"
          @delete-item="handleDeleteItem"
        />
        <CartFooter
          :total-count="totalCount"
          :total-discount="totalDiscount"
          :main-total="mainTotal"
        />
      </div>

      <!-- 右侧面板 -->
      <div class="right-panel">
        <MemberPanel
          ref="memberPanel"
          @open-keyboard="showMemberKeyboard = true"
          @query-member="handleQueryMember"
          @clear-member="handleClearMember"
        />
        <ShortcutPanel
          ref="shortcutPanel"
          @add-shortcut="handleAddShortcut"
          @add-custom="handleAddCustom"
          @add-collected-product="handleAddCollectedProduct"
        />
        <div class="action-grid">
          <button class="btn-big btn-hold" @click="handleHold">
            <i class="el-icon-video-pause"></i> 挂单 (F2)
          </button>
          <button class="btn-big btn-retrieve" @click="showHoldModal = true">
            <i class="el-icon-document"></i> 取单
            <div v-if="pendingOrders.length > 0" class="badge">{{ pendingOrders.length }}</div>
          </button>
          <button class="btn-big btn-checkout" @click="handleCheckout">
            <i class="el-icon-circle-check"></i> 结 算 (Space)
          </button>
        </div>
      </div>
    </div>

    <!-- 弹窗组件 -->
    <MemberKeyboardModal
      :visible="showMemberKeyboard"
      @confirm="handleKeyboardConfirm"
      @close="showMemberKeyboard = false"
    />
    <HoldOrderModal
      :visible="showHoldModal"
      :hold-orders="pendingOrders"
      @restore="handleRestoreOrder"
      @close="showHoldModal = false"
    />
    <CheckoutModal
      :visible="showCheckoutModal"
      :total-amount="mainTotal"
      @pay="handlePay"
      @scan-pay="handleScanPay"
      @close="showCheckoutModal = false"
    />
  </div>
</template>

<script>
import PosHeader from '@/components/pos/Header.vue'
import SearchArea from '@/components/pos/SearchArea.vue'
import CartList from '@/components/pos/CartList.vue'
import CartFooter from '@/components/pos/CartFooter.vue'
import MemberPanel from '@/components/pos/MemberPanel.vue'
import ShortcutPanel from '@/components/pos/ShortcutPanel.vue'
import MemberKeyboardModal from '@/components/pos/MemberKeyboardModal.vue'
import HoldOrderModal from '@/components/pos/HoldOrderModal.vue'
import CheckoutModal from '@/components/pos/CheckoutModal.vue'
import { searchProductStock, searchShopVipUser, saveStoreOrderUp, queryStoreOrderCartUp, computeGiveGold, queryStoreOrderUp } from '@/api/goods/goodCashierApi'
import { pcUnifiedOrder, pcQrCodeUnifiedOrderPre, pcQrCodeUnifiedOrderCheck } from '@/api/goods/goodCashierConfirmApi'
import { getShopId } from '@/api/shop/shopApi'

export default {
  name: 'GoodsPos',
  components: {
    PosHeader,
    SearchArea,
    CartList,
    CartFooter,
    MemberPanel,
    ShortcutPanel,
    MemberKeyboardModal,
    HoldOrderModal,
    CheckoutModal
  },
  data() {
    return {
      cart: [],
      member: null,
      pendingOrders: [],
      showMemberKeyboard: false,
      showHoldModal: false,
      showCheckoutModal: false,
      loading: false,
      currentOrderId: null // 当前订单ID，用于支付状态检查
    }
  },
  mounted() {
    this.initKeyboardShortcuts()
    this.loadPendingOrders()
  },
  computed: {
    totalCount() {
      return this.cart.reduce((sum, item) => sum + item.qty, 0)
    },
    totalDiscount() {
      // 计算折扣逻辑
      return 0
    },
    mainTotal() {
      let total = this.cart.reduce((sum, item) => {
        const price = this.member ? (item.vipPrice || item.price) : item.price
        return sum + (price * item.qty)
      }, 0)
      
      // 会员折扣
      if (this.member && this.member.discount) {
        total = total * (this.member.discount / 100)
      }
      
      return total
    }
  },
  beforeDestroy() {
    this.removeKeyboardShortcuts()
  },
  methods: {
    // 搜索商品
    async handleSearch(value) {
      try {
        const res = await searchProductStock({
          searchValue: value,
          page: 1,
          row: 1
        })
        
        if (res.data && res.data.length > 0) {
          const product = res.data[0]
          this.addToCart({
            id: product.productId,
            name: product.prodName,
            price: parseFloat(product.price || 0),
            vipPrice: parseFloat(product.vipPrice || 0),
            specName: product.specName || '',
            coverPhoto: product.coverPhoto || '',
            valueId: product.valueId,
            productId: product.productId
          })
        } else {
          this.$message.warning('未找到商品')
        }
      } catch (error) {
        console.error('搜索商品失败:', error)
        this.$message.error('搜索商品失败')
      }
    },
    
    // 直接添加商品到购物车（从商品选择对话框）
    handleAddProduct(product) {
      // 从商品对象中提取信息并添加到购物车
      // 优先使用 product 对象中的价格和 valueId，如果没有则使用 defaultSpecValue
      const defaultSpec = product.defaultSpecValue
      
      this.addToCart({
        id: product.productId,
        name: product.prodName,
        price: parseFloat(product.price || defaultSpec.price),
        vipPrice: parseFloat(product.vipPrice || defaultSpec.vipPrice),
        specName: defaultSpec.specName || '',
        coverPhoto: product.coverPhoto,
        valueId: product.valueId || defaultSpec.valueId,
        productId: product.productId
      })
    },
    
    // 添加到购物车
    addToCart(product) {
      const exist = this.cart.find(x => x.id === product.id && x.price === product.price)
      if (exist) {
        exist.qty++
      } else {
        this.cart.push({ ...product, qty: 1 })
      }
    },
    
    // 修改数量
    handleModifyQty(index, delta) {
      this.cart[index].qty += delta
      if (this.cart[index].qty <= 0) {
        this.cart.splice(index, 1)
      }
    },
    
    // 删除商品
    handleDeleteItem(index) {
      this.cart.splice(index, 1)
    },
    
    // 添加常用商品
    handleAddShortcut(item) {
      this.addToCart(item)
    },
    
    // 添加无码商品
    handleAddCustom() {
      this.$prompt('请输入金额', '无码商品', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^\d+(\.\d{1,2})?$/,
        inputErrorMessage: '请输入有效的金额'
      }).then(({ value }) => {
        this.addToCart({
          id: 'custom_' + Date.now(),
          name: '无码商品',
          price: parseFloat(value)
        })
      }).catch(() => {})
    },
    
    // 添加收藏商品
    handleAddCollectedProduct(item) {
      // 直接使用传递过来的商品信息，不再查询
      // ShortcutPanel 已经通过 queryProductAndSpec 查询了商品信息
      this.addToCart({
        id: item.valueId || item.productId || item.specId,
        name: item.prodName || item.name || '',
        price: parseFloat(item.prodPrice || item.price || 0),
        vipPrice: parseFloat(item.vipPrice || item.price || 0),
        specName: item.specName || '',
        coverPhoto: item.coverPhoto || '',
        valueId: item.valueId || item.specId,
        productId: item.productId,
        barCode: item.barCode || ''
      })
    },
    
    // 查询会员
    async handleQueryMember(value) {
      if (!value || !value.trim()) {
        this.$message.warning('请填写会员手机号')
        return
      }
      
      try {
        const res = await searchShopVipUser({
          page: 1,
          row: 1,
          vipUserSearchValue: value.trim()
        })
        
        // 判断返回数据：可能是对象或数组
        const vip = Array.isArray(res.data) ? (res.data.length > 0 ? res.data[0] : null) : (res.data && res.data.vipId ? res.data : null)
        
        if (vip && vip.vipId) {
          this.member = {
            vipId: vip.vipId || '',
            name: vip.vipName || '',
            tel: vip.vipTel || '',
            level: vip.levelName || '',
            sex: vip.sex || '',
            balance: parseFloat(vip.balance || 0),
            integralAmount: parseFloat(vip.integralAmount || 0),
            couponCount: parseInt(vip.couponCount || 0),
            discount: '享9折优惠',
            discountRate: 90
          }
          
          // 如果有会员ID，更新购物车中商品的价格为会员价格
          if (this.member.vipId && this.cart.length > 0) {
            this.updateCartVipPrice()
            // 计算赠送积分
            await this.computeVipUserGold()
          }
          
          this.$refs.memberPanel.setMemberInfo(this.member)
          this.$message.success('会员查询成功')
        } else {
          this.$message.warning('未找到会员信息')
        }
      } catch (error) {
        console.error('查询会员失败:', error)
        this.$message.error('查询会员失败')
      }
    },
    
    // 更新购物车中商品的价格为会员价格
    updateCartVipPrice() {
      this.cart.forEach(item => {
        if (item.vipPrice && item.vipPrice > 0) {
          // 如果商品有会员价格，使用会员价格
          item.price = item.vipPrice
        }
      })
    },
    
    // 计算赠送积分
    async computeVipUserGold() {
      if (!this.member || !this.member.vipId) {
        return
      }
      
      try {
        const amount = this.mainTotal
        const res = await computeGiveGold({
          amount: amount,
          vipId: this.member.vipId
        })
        
        if (res.data) {
          // 更新会员积分信息
          if (res.data.integralAmount !== undefined) {
            this.member.integralAmount = parseFloat(res.data.integralAmount || 0)
          }
          if (res.data.goldCount !== undefined) {
            this.member.goldCount = parseFloat(res.data.goldCount || 0)
          }
          this.$refs.memberPanel.setMemberInfo(this.member)
        }
      } catch (error) {
        console.error('计算赠送积分失败:', error)
      }
    },
    
    // 清除会员
    handleClearMember() {
      this.member = null
    },
    
    // 会员键盘确认
    handleKeyboardConfirm(value) {
      this.$refs.memberPanel.setMemberInput(value)
      this.handleQueryMember(value)
    },
    
    // 挂单
    async handleHold() {
      if (this.cart.length === 0) {
        this.$message.warning('购物车为空')
        return
      }
      
      try {
        const shopId = await getShopId()
        
        // 按照参考代码的格式构建商品列表
        const productStocks = this.cart.map(item => ({
          cateType: item.cateType || '1001',
          productId: item.id,
          prodName: item.name,
          valueId: item.valueId || item.id,
          prodPrice: item.price,
          count: item.qty,
          money: (item.price * item.qty).toFixed(2),
          storeId: item.storeId || '',
          shopId: shopId,
          discountType: item.discountType || '1001',
          discountRate: item.discountRate || '1',
          discountPrice: item.discountPrice || 0,
          vipPrice: item.vipPrice || item.price,
          coverPhoto: item.coverPhoto || '',
          specName: item.specName || ''
        }))
        
        // 构建挂单数据
        const orderData = {
          storeOrderUp: {
            totalPrice: this.mainTotal.toFixed(2),
            state: '0', // 参考代码使用 '0' 表示挂单状态
            vipId: (this.member && this.member.vipId) ? this.member.vipId : '-1',
            shopId: shopId
          },
          productStocks: productStocks
        }
        
        const res = await saveStoreOrderUp(orderData)
        
        if (res.code === 0) {
          this.$message.success(res.msg || '挂单成功')
          this.cart = []
          this.member = null
          this.$refs.memberPanel.clearMember()
          // 重新加载挂单列表
          await this.loadPendingOrders()
        } else {
          this.$message.error(res.msg || '挂单失败')
        }
      } catch (error) {
        console.error('挂单失败:', error)
        this.$message.error(error.msg || '挂单失败')
      }
    },
    
    // 加载挂单列表
    async loadPendingOrders() {
      try {
        const res = await queryStoreOrderUp({
          page: 1,
          row: 50,
          state: '0' // 只查询挂单状态的订单
        })
        
        if (res.data) {
          this.pendingOrders = res.data.map(item => ({
            orderUpId: item.orderUpId,
            time: item.createTime ? new Date(item.createTime).toLocaleTimeString().slice(0, 5) : '',
            total: parseFloat(item.totalPrice || 0).toFixed(2),
            vipId: item.vipId,
            shopId: item.shopId
          }))
        }
      } catch (error) {
        console.error('加载挂单列表失败:', error)
      }
    },
    
    // 恢复挂单
    async handleRestoreOrder(index) {
      try {
        const order = this.pendingOrders[index]
        if (!order || !order.orderUpId) {
          this.$message.error('挂单信息无效')
          return
        }
        
        // 查询挂单商品详情
        const res = await queryStoreOrderCartUp({
          page: 1,
          row: 100,
          orderUpId: order.orderUpId
        })
        
        if (res.data && res.data.length > 0) {
          // 将挂单商品转换为购物车格式
          this.cart = res.data.map(item => ({
            id: item.productId,
            name: item.prodName,
            price: parseFloat(item.prodPrice || 0),
            vipPrice: parseFloat(item.vipPrice || item.prodPrice || 0),
            qty: parseInt(item.count || item.cartNum || 1),
            specName: item.specName || '',
            coverPhoto: item.coverPhoto || '',
            cateType: item.cateType || '1001',
            valueId: item.valueId || item.productId,
            storeId: item.storeId || '',
            discountType: item.discountType || '1001',
            discountRate: item.discountRate || '1',
            discountPrice: parseFloat(item.discountPrice || 0)
          }))
          
          // 如果有会员ID，查询会员信息
          if (order.vipId && order.vipId !== '-1') {
            // 这里可以根据需要查询会员信息
            // 暂时不处理，因为会员信息可能已经过期
          }
          
          // 从挂单列表中移除
          this.pendingOrders.splice(index, 1)
          this.$message.success('取单成功')
        } else {
          this.$message.warning('挂单商品为空')
        }
      } catch (error) {
        console.error('取单失败:', error)
        this.$message.error(error.msg || '取单失败')
      }
    },
    
    // 结算
    handleCheckout() {
      if (this.cart.length === 0) {
        this.$message.warning('购物车为空')
        return
      }
      this.showCheckoutModal = true
    },
    
    // 支付（现金 / 微信 / 支付宝）
    async handlePay(payData) {
      try {
        if (payData.method === 'scangun') {
          this.$message.warning('扫码枪支付请在扫码模式下扫描顾客付款码')
          return
        }

        const shopId = await getShopId()
        
        // 构建商品列表
        const storeOrderCarts = this.cart.map(item => ({
          cateType: item.cateType || '1001', // 默认普通商品
          productId: item.productId || item.id,
          prodName: item.name,
          valueId: item.valueId, // 必须使用 valueId，不能使用 item.id 作为备用
          goodsNum: item.qty,
          storeId: item.storeId || '',
          shopId: shopId,
          discountType: item.discountType || '1001',
          discountRate: item.discountRate || '1',
          discountPrice: item.discountPrice || 0,
          cartType: '3306'
        }))
        
        // 构建订单信息
        const storeOrder = {
          personId: '-1',
          personName: (this.member && this.member.name) || '未知',
          payPrice: payData.amount.toString(),
          discountType: '1001',
          discountRate: '1',
          discountPrice: '0',
          payMethod: payData.method // 记录支付方式（现金/微信/支付宝）
        }
        
        // 构建会员信息
        const vipUsers = this.member ? {
          vipId: this.member.vipId || '',
          vipName: this.member.name || '',
          vipTel: this.member.tel || '',
          levelName: this.member.level || '',
          sex: this.member.sex || '',
          integralAmount: this.member.integralAmount || 0,
          couponCount: this.member.couponCount || 0
        } : {}
        
        // 调用 PC 统一下单接口
        const unifiedOrderParam = {
          storeOrder: storeOrder,
          storeOrderCarts: storeOrderCarts,
          shopId: shopId
        }
        if (this.member && this.member.vipId) {
          unifiedOrderParam.vipUsers = vipUsers
        }
        
        const unifiedOrderRes = await pcUnifiedOrder(unifiedOrderParam)
        
        if (unifiedOrderRes.code !== 0) {
          this.$message.error(unifiedOrderRes.msg || '下单失败')
          return
        }
        
        // 尝试获取订单号（如果后端返回）
        const orderId =
          (unifiedOrderRes.data && unifiedOrderRes.data.orderId) || unifiedOrderRes.orderId || null
        this.currentOrderId = orderId
        
        // 下单成功视为支付完成（由后台根据 payMethod 决定实际支付逻辑）
        this.$message.success(unifiedOrderRes.msg || '下单成功')
        this.cart = []
        this.member = null
        this.showCheckoutModal = false
        this.$refs.memberPanel.clearMember()
        
        // 如果有订单号，则打开小票打印
        if (orderId) {
          this.openOrderPrint({ orderId })
        }
      } catch (error) {
        console.error('下单失败:', error)
        this.$message.error(error.msg || '下单失败')
      }
    },
    
    // 打开订单打印窗口
    openOrderPrint(orderData) {
      if (orderData && orderData.orderId) {
        window.open(`/smallPrint.html#/pages/goods/printSmallOrder?orderId=${orderData.orderId}&autoPrint=Y`)
      }
    },
    
    // 扫码支付（扫码枪 / 被扫）
    handleScanPay(code) {
      this._handleScanGunPay(code)
    },
    
    // 扫码枪预下单 + 轮询确认
    async _handleScanGunPay(scanCode) {
      try {
        const shopId = await getShopId()
        
        // 构建商品列表
        const storeOrderCarts = this.cart.map(item => ({
          cateType: item.cateType || '1001',
          productId: item.productId || item.id,
          prodName: item.name,
          valueId: item.valueId || item.id,
          goodsNum: item.qty,
          storeId: item.storeId || '',
          shopId: shopId,
          discountType: item.discountType || '1001',
          discountRate: item.discountRate || '1',
          discountPrice: item.discountPrice || 0,
          cartType: '3306'
        }))
        
        // 构建订单信息（扫码支付方式）
        const storeOrder = {
          personId: '-1',
          personName: (this.member && this.member.name) || '未知',
          payPrice: this.mainTotal.toString(),
          discountType: '1001',
          discountRate: '1',
          discountPrice: '0',
          payMethod: 'scangun'
        }
        
        // 构建会员信息
        const vipUsers = this.member ? {
          vipId: this.member.vipId || '',
          vipName: this.member.name || '',
          vipTel: this.member.tel || '',
          levelName: this.member.level || '',
          sex: this.member.sex || '',
          integralAmount: this.member.integralAmount || 0,
          couponCount: this.member.couponCount || 0
        } : {}
        
        // 调用扫码枪预下单接口
        const preParam = {
          storeOrder: storeOrder,
          storeOrderCarts: storeOrderCarts,
          shopId: shopId,
          scanCode: scanCode
        }
        if (this.member && this.member.vipId) {
          preParam.vipUsers = vipUsers
        }
        
        const preRes = await pcQrCodeUnifiedOrderPre(preParam)
        if (preRes.code !== 0) {
          this.$message.error(preRes.msg || '预下单失败')
          return
        }
        
        // 保存订单上下文（用于轮询和打印）
        this.currentOrderId =
          (preRes.data && preRes.data.orderId) || preRes.orderId || null
        
        // 轮询检查下单结果
        this._checkQrOrderResult({
          shopId: shopId,
          scanCode: scanCode,
          orderId: this.currentOrderId
        })
      } catch (error) {
        console.error('扫码支付预下单失败:', error)
        this.$message.error(error.msg || '扫码支付预下单失败')
      }
    },
    
    // 轮询查询扫码订单结果
    async _checkQrOrderResult(param) {
      try {
        const res = await pcQrCodeUnifiedOrderCheck(param)
        
        if (res.code === 0) {
          this.$message.success(res.msg || '支付成功')
          this.cart = []
          this.member = null
          this.showCheckoutModal = false
          this.$refs.memberPanel.clearMember()
          
          const orderId =
            (res.data && res.data.orderId) || param.orderId || this.currentOrderId
          if (orderId) {
            this.openOrderPrint({ orderId })
          }
        } else if (res.code === 41) {
          // 支付处理中，继续轮询
          setTimeout(() => {
            this._checkQrOrderResult(param)
          }, 3000)
        } else {
          this.$message.error(res.msg || '扫码支付失败')
        }
      } catch (error) {
        console.error('扫码支付查询失败:', error)
        this.$message.error(error.msg || '扫码支付查询失败')
      }
    },
    
    // 键盘快捷键
    initKeyboardShortcuts() {
      document.addEventListener('keydown', this.handleKeyDown)
    },
    removeKeyboardShortcuts() {
      document.removeEventListener('keydown', this.handleKeyDown)
    },
    handleKeyDown(e) {
      if (e.key === 'F2') {
        e.preventDefault()
        this.handleHold()
      } else if (e.code === 'Space' && !this.showCheckoutModal) {
        e.preventDefault()
        this.handleCheckout()
      } else if (e.key === 'Escape') {
        this.showCheckoutModal = false
        this.showHoldModal = false
        this.showMemberKeyboard = false
      }
    }
  }
}
</script>

<style scoped>
.pos-container {
  background: #e2e8f0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.main-container {
  display: flex;
  height: calc(100vh - 50px);
}

.left-panel {
  flex: 6.5;
  display: flex;
  flex-direction: column;
  background: white;
  border-right: 1px solid #cbd5e1;
}

.right-panel {
  flex: 3.5;
  background: #f1f5f9;
  display: flex;
  flex-direction: column;
  padding: 15px;
  gap: 15px;
}

.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: auto;
}

.btn-big {
  padding: 20px;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
}

.btn-big:active {
  transform: scale(0.98);
}

.btn-hold {
  background: #f59e0b;
}

.btn-hold:hover {
  background: #d97706;
}

.btn-retrieve {
  background: #64748b;
}

.btn-retrieve:hover {
  background: #475569;
}

.btn-checkout {
  grid-column: span 2;
  background: #10b981;
  font-size: 1.4rem;
}

.btn-checkout:hover {
  background: #059669;
}

.badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  font-size: 0.75rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid #f1f5f9;
}
</style>

